// Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const Header = ({ isAuthenticated, handleLogout }) => {
  const [user, setUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false); // Mobile menu toggle
  const dropdownRef = useRef(null);
  const location = useLocation();

  // Decode JWT and set user
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && isAuthenticated) {
      try {
        const decoded = jwtDecode(token);
        setUser(decoded.user || decoded); // Adjust based on your token structure
      } catch (error) {
        console.error('Invalid token:', error);
        localStorage.removeItem('access_token');
      }
    } else {
      setUser(null);
    }
  }, [isAuthenticated]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    return (parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0][0]).toUpperCase();
  };

  return (
    <header className="shadow-md">
      <div className="md:w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* LEFT: Logo / Brand */}
          <div className="flex-shrink-0">
            <img
              src="/logo.png"
              alt="eBhasha Logo"
              className="h-10 w-auto md:h-12" 
            />
          </div>

          {/* CENTER: Main Title */}
          <div className="flex-1 text-center">
            <h1 className="text-xl md:text-3xl font-bold text-blue-500 tracking-wide">eBhasha Jinn</h1>
          </div>

          {/* RIGHT: User Profile & Mobile Menu Toggle */}
          <div className="flex items-center space-x-4">
            {/* Profile (desktop) */}
            {isAuthenticated && user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center space-x-2 md:space-x-3 focus:outline-none"
                  aria-expanded={dropdownOpen}
                >
                  {user.profileImage ? (
                    <img
                      src={user.profileImage}
                      alt="User Avatar"
                      className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover border-2 border-white"
                    />
                  ) : (
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold border-2 border-white">
                      {getInitials(user.name)}
                    </div>
                  )}
                  {/* <span className="hidden md:block font-medium text-sm">
                    {user.name}
                  </span> */}
                </button>

                {/* Dropdown */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white shadow-xl rounded-lg border border-gray-200 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-600 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 text-red-600 hover:bg-gray-50 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="hidden md:block text-sm font-medium px-4 py-2 bg-blue-700 hover:bg-blue-800 rounded-md">
                Login
              </button>
            )}

            {/* Mobile Menu Toggle (Hamburger) */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-blue-700 focus:outline-none"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div className="md:hidden bg-blue-700">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {isAuthenticated && user ? (
              <>
                <div className="flex items-center px-3 py-2">
                  {user.profileImage ? (
                    <img src={user.profileImage} alt="Avatar" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                      {getInitials(user.name)}
                    </div>
                  )}
                  <span className="ml-3 font-medium">{user.name}</span>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-white hover:bg-blue-800 rounded-md"
                >
                  Logout
                </button>
              </>
            ) : (
              <button className="w-full text-left px-3 py-2 text-white hover:bg-blue-800 rounded-md">
                Login
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;